import { useState, useEffect } from 'react'
import { Plus, Trash2, Download, Eye, HelpCircle } from 'lucide-react'
import { LLMTxtFormData, URLItem, ContentSection, DetailedInfoSection } from '../types'
import { generateId } from '../utils/helpers'

interface TooltipProps {
  content: string
  children: React.ReactNode
}

function Tooltip({ content, children }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="cursor-help"
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-10 w-64 p-3 text-sm text-white bg-gray-900 rounded-lg shadow-lg -top-2 left-6 transform -translate-y-full">
          <div className="relative">
            {content}
            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  )
}

interface LLMTxtFormProps {
  onGenerate: (data: LLMTxtFormData) => void
  onPreview: (data: LLMTxtFormData) => void
  loading?: boolean
}

const getDemoData = (): LLMTxtFormData => ({
  website_name: 'llm.txt Generator Platform',
  website_summary: 'A free, user-friendly tool for creating llm.txt files that help AI tools like ChatGPT, Claude, and Gemini better understand your website content. We provide educational resources and best practices for AI SEO.',
  website_url: 'https://llmtext.net',
  detailed_info: [
    {
      id: generateId(),
      content: 'Our platform is designed to make AI SEO accessible to everyone. We believe that every website should be optimized for AI tools, and we provide the tools and knowledge to make that happen. Whether you\'re a small business owner, blogger, or enterprise, our generator helps you create properly formatted llm.txt files in minutes.'
    },
    {
      id: generateId(),
      content: 'We also maintain a comprehensive blog with the latest insights on AI SEO trends, case studies, and best practices. Our educational content helps users understand not just how to create llm.txt files, but why they\'re important for the future of search and discovery.'
    }
  ],
  content_sections: [
    {
      id: generateId(),
      name: 'Tools',
      urls: [
        {
          id: generateId(),
          title: 'llm.txt Generator',
          url: 'https://llmtext.net/generator',
          notes: 'Free tool to create properly formatted llm.txt files'
        },
        {
          id: generateId(),
          title: 'llm.txt Verification Tool',
          url: 'https://llmtext.net/verification',
          notes: 'Check if your existing llm.txt file is accessible and properly formatted'
        }
      ]
    },
    {
      id: generateId(),
      name: 'Educational Resources',
      urls: [
        {
          id: generateId(),
          title: 'What is llm.txt?',
          url: 'https://llmtext.net/what-is-llm-txt',
          notes: 'Complete guide explaining llm.txt files and how they work'
        },
        {
          id: generateId(),
          title: 'Benefits of Using llm.txt',
          url: 'https://llmtext.net/benefits',
          notes: 'Discover why your website needs an llm.txt file'
        },
        {
          id: generateId(),
          title: 'AI SEO Best Practices',
          url: 'https://llmtext.net/ai-seo-best-practices',
          notes: 'Comprehensive guide to optimizing your website for AI tools'
        },
        {
          id: generateId(),
          title: 'Implementation Guide',
          url: 'https://llmtext.net/implementation-guide',
          notes: 'Step-by-step instructions for uploading your llm.txt file'
        }
      ]
    },
    {
      id: generateId(),
      name: 'Blog',
      urls: [
        {
          id: generateId(),
          title: 'AI SEO Blog',
          url: 'https://llmtext.net/blog',
          notes: 'Latest insights, tips, and news about AI SEO and llm.txt'
        }
      ]
    }
  ],
  optional_section: {
    enabled: true,
    urls: [
      {
        id: generateId(),
        title: 'About Us',
        url: 'https://llmtext.net/about',
        notes: 'Learn about our mission to make AI SEO accessible'
      },
      {
        id: generateId(),
        title: 'Contact',
        url: 'https://llmtext.net/contact',
        notes: 'Get in touch with our team'
      }
    ]
  }
})

export function LLMTxtForm({ onGenerate, onPreview, loading }: LLMTxtFormProps) {
  const [formData, setFormData] = useState<LLMTxtFormData>({
    website_name: '',
    website_summary: '',
    website_url: '',
    detailed_info: [],
    content_sections: [
      {
        id: generateId(),
        name: '',
        urls: [{ id: generateId(), title: '', url: '', notes: '' }]
      }
    ],
    optional_section: {
      enabled: false,
      urls: []
    }
  })

  // Expose a global hook for demo loading from parent if needed
  useEffect(() => {
    ;(window as any).setLLMTxtDemoData = () => setFormData(getDemoData())
  }, [])

  const updateFormData = (updates: Partial<LLMTxtFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
  }

  const addDetailedInfo = () => {
    const newSection: DetailedInfoSection = {
      id: generateId(),
      content: ''
    }
    updateFormData({
      detailed_info: [...formData.detailed_info, newSection]
    })
  }

  const updateDetailedInfo = (id: string, content: string) => {
    updateFormData({
      detailed_info: formData.detailed_info.map(section =>
        section.id === id ? { ...section, content } : section
      )
    })
  }

  const removeDetailedInfo = (id: string) => {
    updateFormData({
      detailed_info: formData.detailed_info.filter(section => section.id !== id)
    })
  }

  const addContentSection = () => {
    const newSection: ContentSection = {
      id: generateId(),
      name: '',
      urls: [{ id: generateId(), title: '', url: '', notes: '' }]
    }
    updateFormData({
      content_sections: [...formData.content_sections, newSection]
    })
  }

  const updateContentSection = (id: string, updates: Partial<ContentSection>) => {
    updateFormData({
      content_sections: formData.content_sections.map(section =>
        section.id === id ? { ...section, ...updates } : section
      )
    })
  }

  const removeContentSection = (id: string) => {
    updateFormData({
      content_sections: formData.content_sections.filter(section => section.id !== id)
    })
  }

  const addUrlToSection = (sectionId: string) => {
    const newUrl: URLItem = {
      id: generateId(),
      title: '',
      url: '',
      notes: ''
    }
    updateContentSection(sectionId, {
      urls: [...(formData.content_sections.find(s => s.id === sectionId)?.urls || []), newUrl]
    })
  }

  const updateUrlInSection = (sectionId: string, urlId: string, updates: Partial<URLItem>) => {
    const section = formData.content_sections.find(s => s.id === sectionId)
    if (section) {
      updateContentSection(sectionId, {
        urls: section.urls.map(url =>
          url.id === urlId ? { ...url, ...updates } : url
        )
      })
    }
  }

  const removeUrlFromSection = (sectionId: string, urlId: string) => {
    const section = formData.content_sections.find(s => s.id === sectionId)
    if (section) {
      updateContentSection(sectionId, {
        urls: section.urls.filter(url => url.id !== urlId)
      })
    }
  }

  const addOptionalUrl = () => {
    const newUrl: URLItem = {
      id: generateId(),
      title: '',
      url: '',
      notes: ''
    }
    updateFormData({
      optional_section: {
        ...formData.optional_section,
        urls: [...formData.optional_section.urls, newUrl]
      }
    })
  }

  const updateOptionalUrl = (urlId: string, updates: Partial<URLItem>) => {
    updateFormData({
      optional_section: {
        ...formData.optional_section,
        urls: formData.optional_section.urls.map(url =>
          url.id === urlId ? { ...url, ...updates } : url
        )
      }
    })
  }

  const removeOptionalUrl = (urlId: string) => {
    updateFormData({
      optional_section: {
        ...formData.optional_section,
        urls: formData.optional_section.urls.filter(url => url.id !== urlId)
      }
    })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onGenerate(formData)
  }

  const handlePreview = () => {
    onPreview(formData)
  }





  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <div className="card">
        <h2 className="text-2xl font-semibold text-gray-900 mb-8">Basic Information</h2>

        <div className="space-y-8">
          {/* Website Name and URL - Two columns on larger screens */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="label flex items-center">
                Website Name *
                <Tooltip content="This becomes the main H1 heading in your llm.txt file. Use your brand name or website title exactly as you want AI tools to reference it.">
                  <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                </Tooltip>
              </label>
              <input
                type="text"
                required
                value={formData.website_name}
                onChange={(e) => updateFormData({ website_name: e.target.value })}
                className="input-field text-lg"
                placeholder="My Awesome Website"
              />
              <p className="help-text">
                The main name of your website or project
              </p>
            </div>

            <div>
              <label className="label flex items-center">
                Website URL (Optional)
                <Tooltip content="Your website's main URL. This helps us track analytics and can be useful for AI tools to understand your domain context.">
                  <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                </Tooltip>
              </label>
              <input
                type="url"
                value={formData.website_url}
                onChange={(e) => updateFormData({ website_url: e.target.value })}
                className="input-field text-lg"
                placeholder="https://example.com"
              />
              <p className="help-text">
                Your website's main URL for analytics
              </p>
            </div>
          </div>

          {/* Website Summary - Full width */}
          <div>
            <label className="label flex items-center">
              Website Summary *
              <Tooltip content="This appears as a blockquote in your llm.txt file. Write 1-2 sentences that clearly explain what your website offers. This is often the first thing AI tools read to understand your site.">
                <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
              </Tooltip>
            </label>
            <textarea
              required
              value={formData.website_summary}
              onChange={(e) => updateFormData({ website_summary: e.target.value })}
              className="textarea-field text-lg"
              placeholder="A comprehensive resource for web development tutorials, tools, and best practices. We help developers learn modern web technologies through practical examples and step-by-step guides."
              rows={4}
            />
            <p className="help-text">
              Write a clear, concise summary of what your website is about (1-2 sentences work best)
            </p>
          </div>
        </div>
      </div>

      {/* Detailed Information */}
      <div className="card">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              Detailed Information (Optional)
              <Tooltip content="Add additional context about your website that doesn't fit in the summary. This could include your mission, unique features, target audience, or special services. Each section becomes a paragraph in your llm.txt file.">
                <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
              </Tooltip>
            </h2>
            <p className="text-gray-600 mt-2">
              Add extra context and details that help AI tools understand your website better
            </p>
          </div>
          <button
            type="button"
            onClick={addDetailedInfo}
            className="btn-outline flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </button>
        </div>

        {formData.detailed_info.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-gray-500 mb-4">No detailed sections added yet</p>
            <button
              type="button"
              onClick={addDetailedInfo}
              className="btn-primary flex items-center mx-auto"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Section
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {formData.detailed_info.map((section, index) => (
              <div key={section.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <label className="label text-lg">
                    Section {index + 1}
                  </label>
                  <button
                    type="button"
                    onClick={() => removeDetailedInfo(section.id)}
                    className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                    title="Remove this section"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <textarea
                  value={section.content}
                  onChange={(e) => updateDetailedInfo(section.id, e.target.value)}
                  className="textarea-field text-base"
                  placeholder="Example: We specialize in helping small businesses establish their online presence through modern web technologies. Our tutorials cover everything from basic HTML to advanced React applications, with a focus on practical, real-world examples."
                  rows={6}
                />
                <p className="help-text mt-2">
                  Explain important aspects of your website, services, or unique value proposition
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content Sections */}
      <div className="card">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              Content Sections *
              <Tooltip content="Organize your website's important pages into logical sections. Each section becomes an H2 heading with a list of links. Examples: 'Blog Posts', 'Products', 'Documentation', 'Services'. This helps AI tools understand your site structure.">
                <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
              </Tooltip>
            </h2>
            <p className="text-gray-600 mt-2">
              Organize your most important pages into logical sections for AI tools
            </p>
          </div>
          <button
            type="button"
            onClick={addContentSection}
            className="btn-outline flex items-center"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </button>
        </div>

        <div className="space-y-8">
          {formData.content_sections.map((section, sectionIndex) => (
            <div key={section.id} className="border border-gray-200 rounded-lg p-6 bg-gradient-to-br from-white to-gray-50">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Section {sectionIndex + 1}
                </h3>
                {formData.content_sections.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeContentSection(section.id)}
                    className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                    title="Remove this section"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="mb-6">
                <label className="label flex items-center">
                  Section Name *
                  <Tooltip content="Give this section a clear, descriptive name. Examples: 'Blog Posts', 'Product Catalog', 'Documentation', 'Case Studies', 'Services'. This becomes an H2 heading in your llm.txt file.">
                    <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                  </Tooltip>
                </label>
                <input
                  type="text"
                  required
                  value={section.name}
                  onChange={(e) => updateContentSection(section.id, { name: e.target.value })}
                  className="input-field text-lg"
                  placeholder="Blog Posts, Products, Documentation, Services..."
                />
                <p className="help-text">
                  Choose a clear name that describes this group of pages
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <label className="label text-lg flex items-center">
                    URLs in this section *
                    <Tooltip content="Add the most important pages from this section. Include the full URL (preferably to markdown versions if available) and a clear title. AI tools will use these to understand your content structure.">
                      <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                    </Tooltip>
                  </label>
                  <button
                    type="button"
                    onClick={() => addUrlToSection(section.id)}
                    className="btn-outline text-sm flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add URL
                  </button>
                </div>

                {section.urls.map((url, urlIndex) => (
                  <div key={url.id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-base font-medium text-gray-900">
                        URL {urlIndex + 1}
                      </h4>
                      {section.urls.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUrlFromSection(section.id, url.id)}
                          className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                          title="Remove this URL"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="label flex items-center">
                          Link Title *
                          <Tooltip content="A clear, descriptive title for this page. This is how AI tools will reference this content. Examples: 'Getting Started Guide', 'React Tutorial', 'Pricing Plans'.">
                            <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                          </Tooltip>
                        </label>
                        <input
                          type="text"
                          required
                          value={url.title}
                          onChange={(e) => updateUrlInSection(section.id, url.id, { title: e.target.value })}
                          className="input-field text-base"
                          placeholder="Getting Started Guide"
                        />
                      </div>

                      <div>
                        <label className="label flex items-center">
                          URL *
                          <Tooltip content="The full URL to this page. Preferably link to markdown versions (.md) if available, as AI tools can process these better. Make sure the URL is publicly accessible.">
                            <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                          </Tooltip>
                        </label>
                        <input
                          type="url"
                          required
                          value={url.url}
                          onChange={(e) => updateUrlInSection(section.id, url.id, { url: e.target.value })}
                          className="input-field text-base"
                          placeholder="https://example.com/guide.html"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="label flex items-center">
                        Notes (Optional)
                        <Tooltip content="Add a brief note about this page to help AI tools understand its purpose or importance. Examples: 'Beginner-friendly tutorial', 'Advanced concepts', 'Most popular guide'.">
                          <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
                        </Tooltip>
                      </label>
                      <input
                        type="text"
                        value={url.notes}
                        onChange={(e) => updateUrlInSection(section.id, url.id, { notes: e.target.value })}
                        className="input-field text-base"
                        placeholder="Brief description about this page's content or importance"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optional Section */}
      <div className="card">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center">
              Optional Section
              <Tooltip content="Pages that are useful but not critical. AI tools can skip these if they need to save space or focus on core content. Examples: archived content, secondary resources, or supplementary materials.">
                <HelpCircle className="h-4 w-4 ml-2 text-gray-400" />
              </Tooltip>
            </h2>
            <p className="text-gray-600 mt-2">
              Less critical pages that AI tools can skip if needed
            </p>
          </div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={formData.optional_section.enabled}
              onChange={(e) => updateFormData({
                optional_section: {
                  ...formData.optional_section,
                  enabled: e.target.checked
                }
              })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-5 w-5"
            />
            <span className="ml-3 text-base font-medium text-gray-700">Enable optional section</span>
          </label>
        </div>

        {formData.optional_section.enabled && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <label className="label text-lg">Optional URLs</label>
              <button
                type="button"
                onClick={addOptionalUrl}
                className="btn-outline text-sm flex items-center"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add URL
              </button>
            </div>

            {formData.optional_section.urls.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <p className="text-gray-500 mb-4">No optional URLs added yet</p>
                <button
                  type="button"
                  onClick={addOptionalUrl}
                  className="btn-secondary flex items-center mx-auto"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Optional URL
                </button>
              </div>
            ) : (
              formData.optional_section.urls.map((url, urlIndex) => (
                <div key={url.id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-base font-medium text-gray-900">
                      Optional URL {urlIndex + 1}
                    </h4>
                    <button
                      type="button"
                      onClick={() => removeOptionalUrl(url.id)}
                      className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                      title="Remove this URL"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="label">Link Title *</label>
                      <input
                        type="text"
                        required
                        value={url.title}
                        onChange={(e) => updateOptionalUrl(url.id, { title: e.target.value })}
                        className="input-field text-base"
                        placeholder="Additional Resource"
                      />
                    </div>

                    <div>
                      <label className="label">URL *</label>
                      <input
                        type="url"
                        required
                        value={url.url}
                        onChange={(e) => updateOptionalUrl(url.id, { url: e.target.value })}
                        className="input-field text-base"
                        placeholder="https://example.com/resource.html"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="label">Notes (Optional)</label>
                    <input
                      type="text"
                      value={url.notes}
                      onChange={(e) => updateOptionalUrl(url.id, { notes: e.target.value })}
                      className="input-field text-base"
                      placeholder="Brief description about this optional resource"
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="card bg-gradient-to-br from-primary-50 to-secondary-50 border-primary-200">
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Ready to Generate Your llm.txt File?
          </h3>
          <p className="text-gray-600">
            Preview your file first to make sure everything looks correct, then download it.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading}
            className="btn-outline flex items-center justify-center text-lg px-8 py-4"
          >
            <Eye className="h-5 w-5 mr-3" />
            Preview llm.txt
          </button>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center justify-center text-lg px-8 py-4"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
            ) : (
              <Download className="h-5 w-5 mr-3" />
            )}
            Generate & Download
          </button>
        </div>

        {/* Helpful Links */}
        <div className="border-t border-gray-200 pt-8">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
            Need Help?
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/what-is-llm-txt"
              className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 text-center group"
            >
              <div className="text-primary-600 group-hover:text-primary-700 font-medium mb-2">
                📚 What is llm.txt?
              </div>
              <p className="text-sm text-gray-600">
                Learn the basics and understand how llm.txt works
              </p>
            </a>

            <a
              href="/benefits"
              className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 text-center group"
            >
              <div className="text-primary-600 group-hover:text-primary-700 font-medium mb-2">
                🚀 Benefits & Impact
              </div>
              <p className="text-sm text-gray-600">
                Discover why your website needs an llm.txt file
              </p>
            </a>

            <a
              href="/implementation-guide"
              className="p-4 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors duration-200 text-center group"
            >
              <div className="text-primary-600 group-hover:text-primary-700 font-medium mb-2">
                🛠️ Implementation Guide
              </div>
              <p className="text-sm text-gray-600">
                Step-by-step instructions for uploading your file
              </p>
            </a>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Still have questions? Check out our{' '}
              <a href="/ai-seo-best-practices" className="text-primary-600 hover:text-primary-700 font-medium">
                AI SEO Best Practices Guide
              </a>{' '}
              or browse our{' '}
              <a href="/blog" className="text-primary-600 hover:text-primary-700 font-medium">
                blog for tips and insights
              </a>.
            </p>
          </div>
        </div>
      </div>
    </form>
  )
}
